import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { InteractionStatus, InteractionType, EventType, EventMessage } from '@azure/msal-browser';
import { Component, Inject, OnInit, OnDestroy, HostListener } from '@angular/core';
import { filter, Subject, takeUntil } from 'rxjs';
import { TokenService } from '../../service/token.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { AppInsightsService } from '../../service/app-insights.service'; // Make sure path is correct
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Contract AI';
  isIframe = false;
  loginDisplay = false;
  isMenuOpen = false;
  userName: string | null = null;
  private readonly _destroying$ = new Subject<void>();
  private readonly THEME_KEY = 'contractai_theme';
  private systemThemeQuery: MediaQueryList | null = null;
  selectedSource: 'cba' | 'handbook' | 'both' = (sessionStorage.getItem('contractai_source') as any) || 'both';

  isDarkMode = this.getInitialDarkMode();

  constructor(
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private tokenService: TokenService,
    private spinner: NgxSpinnerService,
    private appInsightsService: AppInsightsService, 
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration
  ) {}

  ngOnInit() {
    this.appInsightsService.logInfo('AppComponent initialized', { source: 'frontend', file: 'app.component.ts', function: 'ngOnInit' });
    this.applyTheme();
    this.systemThemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.systemThemeQuery.addEventListener('change', this.handleSystemThemeChange);

    this.spinner.show();
    this.isIframe = window !== window.parent && !window.opener;

    // testing no_account_error fix
    // const accounts = this.authService.instance.getAllAccounts();
    // if (accounts.length > 0) {
    //   this.authService.instance.setActiveAccount(accounts[0]);
    // }

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.appInsightsService.logInfo('MSAL interaction completed', { source: 'frontend', file: 'app.component.ts', function: 'ngOnInit' });

        const accounts = this.authService.instance.getAllAccounts();
        if (accounts.length > 0 && !this.authService.instance.getActiveAccount()) {
          this.authService.instance.setActiveAccount(accounts[0]);
        }

        const activeAccount = this.authService.instance.getActiveAccount();
        const claims: any = (activeAccount as any)?.idTokenClaims;
        const policy = (claims?.tfp || claims?.acr || '').toString();

        if (policy && policy.toLowerCase() === environment.passwordResetPolicy.toLowerCase()) {
          this.appInsightsService.logInfo('Password reset completed; redirecting to sign-in policy login', {
            source: 'frontend',
            file: 'app.component.ts',
            function: 'ngOnInit',
            policy,
            passwordResetPolicy: environment.passwordResetPolicy,
            signInPolicy: environment.signInPolicy,
          });

          this.tokenService.clearAccessToken();
          this.authService.instance.clearCache();

          this.authService.loginRedirect({
            authority: environment.authorityURI,
            scopes: [`${environment.scopeUri}/${environment.scope}`],
            prompt: 'login',
          });
          return;
        }

        this.setLoginDisplay();
      });

    // Listen for real login events
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        sessionStorage.setItem('justLoggedIn', 'true');
        this.setLoginDisplay();
        //this.tokenService.setAccessToken();
      });

    
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_FAILURE),
        takeUntil(this._destroying$)
      )
      .subscribe((msg: EventMessage) => {
        const errorString = (msg.error as any)?.errorMessage || (msg.error as any)?.message || String(msg.error || '');

        this.appInsightsService.logError('MSAL LOGIN_FAILURE received', msg.error, {
          source: 'frontend',
          file: 'app.component.ts',
          function: 'ngOnInit',
          errorString
        });

        if (errorString.includes('AADB2C90118')) {
          const resetAuthority = environment.authorityURI.replace(
            environment.signInPolicy,
            environment.passwordResetPolicy
          );

          this.appInsightsService.logInfo('Redirecting to B2C password reset policy', {
            source: 'frontend',
            file: 'app.component.ts',
            function: 'ngOnInit',
            resetAuthority,
            passwordResetPolicy: environment.passwordResetPolicy
          });

          this.authService.loginRedirect({
            authority: resetAuthority,
            scopes: [`${environment.scopeUri}/${environment.scope}`],
          });
        }
      });

    this.tokenService.getUserInfo.subscribe({
      next: (info) => {
        if (info) {
          this.userName = info.user_name;
          this.appInsightsService.logInfo('User info loaded front', { source: 'frontend', file: 'app.component.ts', function: 'ngOnInit', user_name: this.userName });
          this.spinner.hide();
        }
      },
      error: (err) => {
        this.appInsightsService.logError('Failed to load user info front', err, { source: 'frontend', file: 'app.component.ts', function: 'ngOnInit' });
        this.spinner.hide();
      }
    });
  }

  login() {
    try {
      this.appInsightsService.logInfo('Login initiated', { source: 'frontend', file: 'app.component.ts', function: 'login' });
      this.authService.loginRedirect();
    } catch (error) {
      this.appInsightsService.logError('Login failed', error, { source: 'frontend', file: 'app.component.ts', function: 'login' });
    }
  }

  logout() {
    try {
      this.appInsightsService.logInfo('Logout initiated', { source: 'frontend', file: 'app.component.ts', function: 'logout' });
      this.spinner.show();

      if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
        this.authService.logoutPopup({
          postLogoutRedirectUri: '/',
          mainWindowRedirectUri: '/',
        });
      } else {
        this.authService.logoutRedirect({ postLogoutRedirectUri: '/' });
      }
    } catch (error) {
      this.appInsightsService.logError('Logout failed', error, { source: 'frontend', file: 'app.component.ts', function: 'logout' });
      this.spinner.hide();
    }
  }

  
  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
    this.appInsightsService.logInfo('Login display set', { source: 'frontend', file: 'app.component.ts', function: 'setLoginDisplay', loginDisplay: this.loginDisplay });
    this.tokenService.setAccessToken();
  }

  ngOnDestroy(): void {
    this.appInsightsService.logInfo('AppComponent destroyed', { source: 'frontend', file: 'app.component.ts', function: 'ngOnDestroy' });
    this.systemThemeQuery?.removeEventListener('change', this.handleSystemThemeChange);
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  toggleTheme(): void {
    this.isDarkMode = !this.isDarkMode;
    localStorage.setItem(this.THEME_KEY, this.isDarkMode ? 'dark' : 'light');
    this.applyTheme();

    if (this.isDarkMode) {
      this.appInsightsService.logInfo('Dark mode enabled', { source: 'frontend', file: 'app.component.ts', function: 'toggleTheme' });
    } else {
      this.appInsightsService.logInfo('Light mode enabled', { source: 'frontend', file: 'app.component.ts', function: 'toggleTheme' });
    }
  }

  private applyTheme(): void {
    [document.documentElement, document.body].forEach(element => {
      element.classList.toggle('dark-mode', this.isDarkMode);
      element.classList.toggle('light-mode', !this.isDarkMode);
    });
  }

  private getInitialDarkMode(): boolean {
    const savedTheme = localStorage.getItem(this.THEME_KEY);
    if (savedTheme === 'dark' || savedTheme === 'light') {
      return savedTheme === 'dark';
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  private readonly handleSystemThemeChange = (event: MediaQueryListEvent): void => {
    if (localStorage.getItem(this.THEME_KEY)) {
      return;
    }

    this.isDarkMode = event.matches;
    this.applyTheme();
  };

  // toggleSource(): void {
  //   this.selectedSource = this.selectedSource === 'cba' ? 'handbook' : 'cba';
  //   sessionStorage.setItem('contractai_source', this.selectedSource);

  //   // Notify HomeComponent (same tab) to reset conversation + clear UI
  //   window.dispatchEvent(new CustomEvent('contractai-source-changed', { detail: { source: this.selectedSource } }));
  // }

  setSource(source: 'cba' | 'handbook' | 'both'): void {
    this.selectedSource = source;
    sessionStorage.setItem('contractai_source', this.selectedSource);

    window.dispatchEvent(
      new CustomEvent('contractai-source-changed', {
        detail: { source: this.selectedSource }
      })
    );

    this.closeMenu();
  }

  toggleMenu(): void {
    console.log("toggleMenu clicked", this.isMenuOpen);
    this.isMenuOpen = !this.isMenuOpen;
  }

  closeMenu(): void {
    this.isMenuOpen = false;
  }

  // close menu when clicking anywhere else
  @HostListener('document:click')
  onDocumentClick(): void {
    if (this.isMenuOpen) {
      this.closeMenu();
    }
  }
}
